import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Camera, Info, Loader2, Square, Video, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { GUIDE_DISPLAY_BOOST } from "./carGuideTypes";
import { photoSessionTheme as theme } from "./photoSessionTheme";
import { psLog, psWarn } from "./photoSessionDebug";
import { isNativeTabletViewport } from "@/hooks/useIsNativeTablet";

const TARGET_WIDTH = 1280;
const TARGET_HEIGHT = 720;
const VIDEO_READY_RETRIES = 16;
const VIDEO_READY_RETRY_MS = 60;

export interface PhotoSessionCameraLabels {
  takePhoto?: string;
  startCamera?: string;
  cameraStarting?: string;
  cameraError?: string;
  fallbackLabel?: string;
  landscapeGuide?: string;
  landscapeFormatHint?: string;
  processingPhoto?: string;
  hideGuide?: string;
  showGuide?: string;
  cameraNotReady?: string;
  startRecording?: string;
  stopRecording?: string;
  recording?: string;
  engineChecklistTitle?: string;
  engineChecklistIdle?: string;
  engineChecklistFilm?: string;
}

export type PhotoSessionCameraHandle = {
  takePhoto: () => void;
};

export type GuideFrameMode = "guided" | "optional" | "none";

interface PhotoSessionCameraViewProps {
  onCapture: (blob: Blob) => void | Promise<void>;
  onCaptureFailed?: (reason: string) => void;
  labels?: PhotoSessionCameraLabels;
  active?: boolean;
  canCaptureMore?: boolean;
  /** 16:9 frame + vehicle guide (exterior/interior only). */
  showLandscapeGuide?: boolean;
  /** Silhouette overlay for guided shots. */
  guideImageSrc?: string | null;
  guideScale?: number;
  guideWidthPct?: number;
  guideMaxHeightPct?: number;
  guideFrameMode?: GuideFrameMode;
  /** When false, parent renders shutter (modal layer). */
  showShutter?: boolean;
  /** Engine shot uses short video instead of still. */
  captureMode?: "photo" | "video";
  maxVideoSeconds?: number;
  onRecordingChange?: (recording: boolean) => void;
  className?: string;
}

export const PhotoSessionCameraView = forwardRef<
  PhotoSessionCameraHandle,
  PhotoSessionCameraViewProps
>(function PhotoSessionCameraView(
  {
    onCapture,
    onCaptureFailed,
    labels = {},
    active = true,
    canCaptureMore = true,
    showLandscapeGuide = false,
    guideImageSrc = null,
    guideScale = 1,
    guideWidthPct = 90,
    guideMaxHeightPct = 76,
    guideFrameMode = "none",
    showShutter = true,
    captureMode = "photo",
    maxVideoSeconds = 10,
    onRecordingChange,
    className,
  },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const capturingRef = useRef(false);
  const canCaptureMoreRef = useRef(canCaptureMore);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamReady, setStreamReady] = useState(false);
  const [useFileFallback, setUseFileFallback] = useState(false);
  const [showFrozenFrame, setShowFrozenFrame] = useState(false);
  const [guideOpen, setGuideOpen] = useState(true);
  const [shutterBusy, setShutterBusy] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordElapsed, setRecordElapsed] = useState(0);

  canCaptureMoreRef.current = canCaptureMore;
  const isVideoMode = captureMode === "video";

  useEffect(() => {
    onRecordingChange?.(isRecording);
  }, [isRecording, onRecordingChange]);

  useEffect(() => {
    if (!active) setGuideOpen(true);
  }, [active]);

  // Always call the latest callbacks – MediaRecorder.onstop can fire after the
  // parent re-rendered or even while the session is closing (Klar while recording).
  const onCaptureRef = useRef(onCapture);
  onCaptureRef.current = onCapture;
  const onCaptureFailedRef = useRef(onCaptureFailed);
  onCaptureFailedRef.current = onCaptureFailed;

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreamReady(false);
  }, []);

  const startStream = useCallback(async () => {
    if (!active || streamRef.current) return;
    setIsStarting(true);
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: TARGET_WIDTH, min: 640 },
          height: { ideal: TARGET_HEIGHT, min: 480 },
        },
        audio: false,
      });
      streamRef.current = stream;
      setStreamReady(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`${labels.cameraError ?? "Could not start camera"}: ${msg}`);
      setUseFileFallback(true);
    } finally {
      setIsStarting(false);
    }
  }, [active, labels.cameraError]);

  useEffect(() => {
    if (!active) {
      stopStream();
      return;
    }
    startStream();
    return () => stopStream();
  }, [active, startStream, stopStream]);

  useEffect(() => {
    if (!streamReady || !streamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    video.srcObject = streamRef.current;
    video.play().catch(() => {});
  }, [streamReady]);

  // Engine clip should carry audio (engine sound). Request the microphone only
  // when the video guide is shown so photo shots never prompt for mic permission.
  // Native: RECORD_AUDIO / NSMicrophoneUsageDescription must be declared (manifest/plist)
  // or WebView never shows a prompt and recording stays silent.
  useEffect(() => {
    if (!isVideoMode || !streamReady || !active) return;
    const stream = streamRef.current;
    if (!stream || stream.getAudioTracks().length > 0) return;
    let cancelled = false;
    void navigator.mediaDevices
      .getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      })
      .then((audioStream) => {
        const track = audioStream.getAudioTracks()[0];
        if (!track) return;
        if (cancelled || streamRef.current !== stream) {
          audioStream.getTracks().forEach((t) => t.stop());
          return;
        }
        // Keep the track enabled; some WebViews leave it muted by default.
        track.enabled = true;
        stream.addTrack(track);
        psLog("camera.video.audio_ready", {
          label: track.label || "",
          readyState: track.readyState,
        });
      })
      .catch((e) => {
        // Permission denied / no mic: record video without sound.
        psWarn("camera.video.audio_unavailable", {
          err: e instanceof Error ? e.message : String(e),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [isVideoMode, streamReady, active]);

  const finishCapture = useCallback(() => {
    capturingRef.current = false;
    setShutterBusy(false);
    setShowFrozenFrame(false);
  }, []);

  const tryCaptureFrame = useCallback(
    (_attempt: number): boolean => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || !streamRef.current) return false;

      if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
        return false;
      }

      const w = video.videoWidth;
      const h = video.videoHeight;
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return false;

      ctx.drawImage(video, 0, 0, w, h);
      setShowFrozenFrame(true);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            void Promise.resolve(onCaptureRef.current(blob))
              .catch(() => {
                onCaptureFailedRef.current?.("capture_failed");
              })
              .finally(() => {
                finishCapture();
              });
          } else {
            onCaptureFailedRef.current?.("toBlob_null");
            finishCapture();
          }
        },
        "image/jpeg",
        0.92,
      );
      return true;
    },
    [finishCapture],
  );

  const clearRecordTimers = useCallback(() => {
    if (recordTimerRef.current) {
      clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
  }, []);

  const stopVideoRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }
  }, []);

  const startVideoRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream || !canCaptureMoreRef.current) {
      onCaptureFailed?.("max_reached");
      return;
    }
    if (capturingRef.current || isRecording) return;

    chunksRef.current = [];
    const hasAudio = stream.getAudioTracks().length > 0;
    // MP4/H.264 only — WebM often plays as a black screen on Safari Mac.
    const candidates = hasAudio
      ? ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4"]
      : ["video/mp4;codecs=avc1.42E01E", "video/mp4"];
    let mime =
      typeof MediaRecorder !== "undefined"
        ? candidates.find((m) => MediaRecorder.isTypeSupported(m))
        : undefined;
    if (!mime || typeof MediaRecorder === "undefined") {
      setUseFileFallback(true);
      setError(
        labels.cameraError ??
          "Video recording is not supported – upload a short MP4/MOV clip instead",
      );
      onCaptureFailed?.("capture_failed");
      return;
    }
    try {
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        clearRecordTimers();
        setIsRecording(false);
        setRecordElapsed(0);
        // Strip codec params so backend ALLOWED_VIDEO_TYPES matches.
        const rawMime = (recorder.mimeType || "video/mp4").toLowerCase();
        const mimeType = rawMime.startsWith("video/quicktime")
          ? "video/quicktime"
          : "video/mp4";
        const blob = new Blob(chunksRef.current, { type: mimeType });
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        if (blob.size < 1000) {
          onCaptureFailedRef.current?.("capture_failed");
          finishCapture();
          return;
        }
        psLog("camera.video.recorded", { size: blob.size, type: mimeType });
        void Promise.resolve(onCaptureRef.current(blob))
          .catch(() => onCaptureFailedRef.current?.("capture_failed"))
          .finally(() => finishCapture());
      };

      capturingRef.current = true;
      setShutterBusy(true);
      setIsRecording(true);
      setRecordElapsed(0);
      recorder.start(200);
      recordTimerRef.current = setInterval(() => {
        setRecordElapsed((s) => s + 1);
      }, 1000);
      stopTimeoutRef.current = setTimeout(() => {
        stopVideoRecording();
      }, Math.max(1, maxVideoSeconds) * 1000);
    } catch (e) {
      psWarn("camera.video.record_failed", {
        err: e instanceof Error ? e.message : String(e),
      });
      onCaptureFailed?.("capture_failed");
      finishCapture();
    }
  }, [
    clearRecordTimers,
    finishCapture,
    isRecording,
    labels.cameraError,
    maxVideoSeconds,
    onCapture,
    onCaptureFailed,
    stopVideoRecording,
  ]);

  const capture = useCallback(() => {
    psLog("camera.capture.invoked", { mode: captureMode });

    if (isVideoMode) {
      if (isRecording) {
        stopVideoRecording();
        return;
      }
      startVideoRecording();
      return;
    }

    if (!canCaptureMoreRef.current) {
      onCaptureFailed?.("max_reached");
      return;
    }
    if (capturingRef.current) return;

    capturingRef.current = true;
    setShutterBusy(true);

    const run = (attempt: number) => {
      if (tryCaptureFrame(attempt)) return;
      if (attempt < VIDEO_READY_RETRIES) {
        window.setTimeout(() => run(attempt + 1), VIDEO_READY_RETRY_MS);
        return;
      }
      psWarn("camera.capture.abort", { reason: "video_not_ready" });
      onCaptureFailed?.("video_not_ready");
      finishCapture();
    };
    run(0);
  }, [
    captureMode,
    finishCapture,
    isRecording,
    isVideoMode,
    onCaptureFailed,
    startVideoRecording,
    stopVideoRecording,
    tryCaptureFrame,
  ]);

  useImperativeHandle(ref, () => ({ takePhoto: capture }), [capture]);

  useEffect(() => {
    return () => {
      clearRecordTimers();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          // ignore
        }
      }
    };
  }, [clearRecordTimers]);

  const showLiveCamera = streamReady && active && !useFileFallback;

  const [guideViewportFactor, setGuideViewportFactor] = useState(1);
  useEffect(() => {
    const update = () => {
      if (!isNativeTabletViewport()) {
        setGuideViewportFactor(1);
        return;
      }
      const shortSide = Math.min(window.innerWidth, window.innerHeight);
      setGuideViewportFactor(Math.min(1, 600 / shortSide));
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const guideBoost = GUIDE_DISPLAY_BOOST * guideViewportFactor;
  const guideDisplayWidthPct = Math.min(100, guideWidthPct * guideBoost);
  const guideDisplayMaxHeightPct = Math.min(
    88,
    guideMaxHeightPct * guideBoost,
  );
  const guideDisplayScale = guideScale * guideBoost;

  return (
    <div className={cn("relative min-h-0 min-w-0 h-full w-full", className)}>
      {/* Preview — clipped */}
      <div
        className="absolute inset-0 rounded-xl overflow-hidden"
        style={{ backgroundColor: "#000", border: `1px solid ${theme.border}` }}
      >
        {!streamReady && !error && active && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center z-10"
            style={{ backgroundColor: theme.surface }}
          >
            {isStarting ? (
              <div className="flex flex-col items-center gap-3 px-4">
                <Loader2
                  className="h-10 w-10 animate-spin"
                  style={{ color: theme.accent }}
                />
                <p className="text-sm text-center" style={{ color: theme.textMuted }}>
                  {labels.cameraStarting ?? "Starting camera…"}
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={startStream}
                className="px-6 py-3 rounded-xl font-semibold text-sm"
                style={{ backgroundColor: theme.accent, color: theme.accentFg }}
              >
                {labels.startCamera ?? "Start camera"}
              </button>
            )}
          </div>
        )}

        {error && useFileFallback && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 z-10"
            style={{ backgroundColor: theme.surface }}
          >
            <p className="text-sm text-center" style={{ color: theme.error }}>
              {error}
            </p>
            <input
              type="file"
              accept={isVideoMode ? "video/mp4,video/quicktime" : "image/*"}
              capture="environment"
              className="text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                void Promise.resolve(onCapture(file)).catch(() =>
                  onCaptureFailed?.("capture_failed"),
                );
              }}
            />
          </div>
        )}

        {showLiveCamera && (
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className={cn(
                "absolute inset-0 w-full h-full object-cover pointer-events-none",
                showFrozenFrame && "invisible",
              )}
              style={{ pointerEvents: "none", zIndex: 0 }}
            />
        )}

        <canvas
          ref={canvasRef}
          className={cn(
            showFrozenFrame
              ? "absolute inset-0 w-full h-full object-cover pointer-events-none z-10"
              : "hidden",
          )}
        />

        {showFrozenFrame && (
          <div
            className="absolute inset-0 z-20 pointer-events-none"
            style={{ backgroundColor: "rgba(0,0,0,0.45)" }}
            aria-hidden
          />
        )}
      </div>

      {/* Interactive overlays — OUTSIDE overflow-hidden (touch-safe) */}
      {showLiveCamera &&
        !showFrozenFrame &&
        showLandscapeGuide &&
        guideFrameMode === "guided" &&
        guideImageSrc && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10 flex items-center justify-center"
            style={{
              width: `min(${guideDisplayWidthPct}%, 100%)`,
              maxHeight: `min(${guideDisplayMaxHeightPct}%, calc(100% - 4.5rem))`,
              aspectRatio: "16 / 9",
            }}
            aria-hidden
          >
            <img
              src={guideImageSrc}
              alt=""
              draggable={false}
              className="w-full h-full object-contain select-none"
              style={{
                opacity: 0.92,
                mixBlendMode: "screen",
                transform: `scale(${guideDisplayScale})`,
                transformOrigin: "center center",
              }}
            />
          </div>
        )}

      {showLiveCamera &&
        !showFrozenFrame &&
        showLandscapeGuide &&
        guideFrameMode === "optional" && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none border border-dashed border-white/25 rounded-sm z-10"
            style={{
              width: "min(86%, 100%)",
              maxHeight: "min(72%, calc(100% - 5rem))",
              aspectRatio: "16 / 9",
            }}
            aria-hidden
          />
        )}

      {showLiveCamera && !showFrozenFrame && showLandscapeGuide && guideOpen && (
        <div
          className="absolute bottom-16 left-2 right-24 z-40 max-w-md mx-auto rounded-lg px-3 py-2 pr-9 text-center touch-manipulation"
          style={{ backgroundColor: "rgba(0,0,0,0.82)" }}
        >
          <button
            type="button"
            className="absolute top-1 right-1 p-2 rounded-md"
            style={{ color: theme.textMuted }}
            onClick={() => setGuideOpen(false)}
            aria-label={labels.hideGuide ?? "Hide guide"}
          >
            <X className="h-4 w-4" />
          </button>
          {isVideoMode ? (
            <div className="text-left space-y-1">
              <p
                className="text-[11px] leading-snug font-semibold"
                style={{ color: theme.text }}
              >
                {labels.engineChecklistTitle ?? "Filma motorn (~10 sek)"}
              </p>
              <p className="text-[10px] leading-snug" style={{ color: theme.textMuted }}>
                {labels.engineChecklistIdle ??
                  "1. Sätt på motorn i tomgång"}
              </p>
              <p className="text-[10px] leading-snug" style={{ color: theme.textMuted }}>
                {labels.engineChecklistFilm ??
                  "2. Filma motorrummet stadigt i cirka 10 sekunder"}
              </p>
            </div>
          ) : (
            <>
              <p className="text-[11px] leading-snug font-medium" style={{ color: theme.text }}>
                {labels.landscapeGuide}
              </p>
              <p className="text-[10px] leading-snug mt-0.5" style={{ color: theme.textMuted }}>
                {labels.landscapeFormatHint}
              </p>
            </>
          )}
        </div>
      )}

      {isRecording && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 rounded-full bg-red-600/90 px-3 py-1 text-xs font-semibold text-white tabular-nums">
          {labels.recording ?? "Spelar in"} {recordElapsed}s / {maxVideoSeconds}s
        </div>
      )}

      {showLiveCamera && !showFrozenFrame && showLandscapeGuide && !guideOpen && (
        <button
          type="button"
          className="absolute bottom-16 left-3 z-40 h-10 w-10 rounded-full flex items-center justify-center shadow-md touch-manipulation"
          style={{ backgroundColor: "rgba(0,0,0,0.82)", color: theme.text }}
          onClick={() => setGuideOpen(true)}
          aria-label={labels.showGuide ?? "Show guide"}
        >
          <Info className="h-4 w-4" />
        </button>
      )}

      {/* Round shutter (optional — modal renders its own when showShutter=false) */}
      {showShutter && showLiveCamera && (
        <button
          type="button"
          onClick={capture}
          className="absolute bottom-3 right-3 z-50 h-[4.25rem] w-[4.25rem] rounded-full flex items-center justify-center shadow-2xl active:scale-95 transition-transform border-4 touch-manipulation"
          style={{
            backgroundColor: isRecording ? "#dc2626" : theme.accent,
            color: theme.accentFg,
            borderColor: "rgba(255,255,255,0.35)",
            opacity: (!isVideoMode && (shutterBusy || !canCaptureMore)) ? 0.65 : 1,
          }}
          aria-label={
            isVideoMode
              ? isRecording
                ? (labels.stopRecording ?? "Stoppa inspelning")
                : (labels.startRecording ?? "Spela in")
              : shutterBusy
                ? (labels.processingPhoto ?? "Processing")
                : (labels.takePhoto ?? "Take photo")
          }
        >
          {isVideoMode ? (
            isRecording ? (
              <Square className="h-8 w-8 fill-current" />
            ) : shutterBusy ? (
              <Loader2 className="h-9 w-9 animate-spin" />
            ) : (
              <Video className="h-9 w-9" />
            )
          ) : shutterBusy ? (
            <Loader2 className="h-9 w-9 animate-spin" />
          ) : (
            <Camera className="h-9 w-9" />
          )}
        </button>
      )}
    </div>
  );
});
